import type { CommandEnumOption } from '@/composables/api/useLocationZonePlayerInspectorApi';

/**
 * Client-side mirror of the backend command gating
 * ({@see App\Services\LocationZonePlayerCommandIssuer} +
 * {@see App\Models\LocationZonePlayerCommandEnum}). Lets the issue UI grey out /
 * annotate commands a selected player can't run before submitting. The server
 * re-checks and is authoritative — this is just immediate operator feedback.
 */

// Player type ids — mirror App\Models\PlayerType. Only PHYSICAL and VIRTUAL
// players consume the command list today.
export const PLAYER_TYPE_PHYSICAL = 1;
export const PLAYER_TYPE_VIRTUAL = 2;
const COMMANDABLE_PLAYER_TYPES = [PLAYER_TYPE_PHYSICAL, PLAYER_TYPE_VIRTUAL];

export interface CommandTarget {
    /** The player's reported software version (player_data_version). */
    version: string | null;
    /** players.player_type_id (from the record's nested player_type). */
    playerTypeId: number | null;
}

/** A version bound counts as present only when it's a non-empty trimmed string ('' = no bound). */
function hasBound(bound: string | null): boolean {
    return bound !== null && bound.trim() !== '';
}

/**
 * Collapse a dotted version to a comparable integer weight, mirroring the
 * backend: strip a known build-channel suffix (".VP" / ".DEBUG" — virtual
 * players report e.g. "50.1.6.VP"), require a strict numeric core, then
 * major*1e6 + minor*1e3 + patch. Returns null for empty / unparseable input.
 */
export function versionWeight(version: string | null): number | null {
    const v = (version ?? '').trim().replace(/\.(?:VP|DEBUG)\b.*$/i, '');
    if (!/^[0-9]+(?:\.[0-9]+)*$/.test(v)) {
        return null;
    }
    const [major = 0, minor = 0, patch = 0] = v.split('.').map((n) => parseInt(n, 10));
    return major * 1_000_000 + minor * 1_000 + patch;
}

export function appliesToVersion(command: CommandEnumOption, version: string | null): boolean {
    const hasMinimum = hasBound(command.version_minimum);
    const hasMaximum = hasBound(command.version_maximum);
    if (!hasMinimum && !hasMaximum) {
        return true;
    }
    const player = versionWeight(version);
    if (player === null) {
        return false;
    }
    if (hasMinimum) {
        const minimum = versionWeight(command.version_minimum);
        if (minimum === null || player < minimum) {
            return false;
        }
    }
    if (hasMaximum) {
        const maximum = versionWeight(command.version_maximum);
        if (maximum === null || player > maximum) {
            return false;
        }
    }
    return true;
}

export function appliesToPlayerType(command: CommandEnumOption, playerTypeId: number | null): boolean {
    if (command.is_virtual_player_command_only && playerTypeId !== PLAYER_TYPE_VIRTUAL) {
        return false;
    }
    if (command.is_hardware_player_command_only && playerTypeId !== PLAYER_TYPE_PHYSICAL) {
        return false;
    }
    return true;
}

function versionBoundLabel(command: CommandEnumOption): string {
    const hasMinimum = hasBound(command.version_minimum);
    const hasMaximum = hasBound(command.version_maximum);
    if (hasMinimum && hasMaximum) {
        return `version ${command.version_minimum}–${command.version_maximum}`;
    }
    if (hasMinimum) {
        return `version ${command.version_minimum}+`;
    }
    return `version ≤ ${command.version_maximum}`;
}

/**
 * Why `command` can't be issued to `target`, or null if it can. Order mirrors
 * the issuer: command-intrinsic blocks (issuable, legacy-only), then
 * player-type-can-receive-at-all, then the command's type / version bounds.
 */
export function commandGateReason(command: CommandEnumOption, target: CommandTarget): string | null {
    if (!command.is_issuable) {
        return 'Not an issuable command.';
    }
    // Optional field — absent (backend not merged yet) means no legacy gating.
    if (command.requires_legacy_handler === true) {
        return 'Runs on the legacy server only — not supported here yet.';
    }
    if (target.playerTypeId === null || !COMMANDABLE_PLAYER_TYPES.includes(target.playerTypeId)) {
        return 'This player type cannot receive commands yet.';
    }
    if (!appliesToPlayerType(command, target.playerTypeId)) {
        return command.is_virtual_player_command_only
            ? 'Virtual players only.'
            : 'Hardware players only.';
    }
    if (!appliesToVersion(command, target.version)) {
        const reported = (target.version ?? '').trim() === '' ? 'unknown' : target.version;
        return `Requires ${versionBoundLabel(command)} (player on ${reported}).`;
    }
    return null;
}

export function canIssueCommand(command: CommandEnumOption, target: CommandTarget): boolean {
    return commandGateReason(command, target) === null;
}

/**
 * Why `command` should render disabled in the picker for this set of targets,
 * or null to leave it enabled. A command stays pickable when ANY target can
 * take it (partial eligibility — the server reports per-player skipped[]);
 * it's disabled only when every target fails, with the single distinct reason
 * when they all agree, or a generic line when the reasons differ. No targets →
 * null (nothing to gate against). Single-player callers pass a one-element
 * array — same path as bulk.
 */
export function bulkOptionGateReason(command: CommandEnumOption, targets: CommandTarget[]): string | null {
    if (targets.length === 0) {
        return null;
    }
    const reasons: string[] = [];
    for (const target of targets) {
        const reason = commandGateReason(command, target);
        if (reason === null) {
            return null;
        }
        reasons.push(reason);
    }
    const distinctReasons = [...new Set(reasons)];
    return distinctReasons.length === 1 ? distinctReasons[0] : 'Not available for any selected player.';
}
